import React, { useEffect, useState } from 'react'
import { Button, Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { organizationService } from '../../services/organization.service'
import { Organization } from '../../types'
import { InputSwitch } from '../common/InputSwitch'
import { InputText } from '../common/InputText'

interface OrganizationEditModalProps {
  show: boolean
  onHide: () => void
  organization?: Organization | null
  onSuccess: (organization: Organization) => void
  onError: (error: any) => void
}

interface OrganizationFormData {
  name: string
  is_active: boolean
}

const OrganizationEditModal: React.FC<OrganizationEditModalProps> = ({
  show,
  onHide,
  organization,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    is_active: true,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEdit = !!organization

  useEffect(() => {
    if (!show) {
      return
    }
    if (organization) {
      setFormData({
        name: organization.name || '',
        is_active: organization.is_active,
      })
    } else {
      setFormData({
        name: '',
        is_active: true,
      })
    }
    setErrors({})
  }, [show, organization])

  const handleInputChange = (field: keyof OrganizationFormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})
    try {
      const payload: Partial<Organization> = {
        name: formData.name.trim(),
        is_active: formData.is_active,
      }
      const savedOrganization =
        isEdit && organization
          ? await organizationService.update(organization.id, payload)
          : await organizationService.create(payload)
      onSuccess(savedOrganization)
      onHide()
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data)
      } else {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? t('edit_organization') : t('create_new_organization')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <InputText
                title={t('name')}
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                error={errors.name}
                autocomplete="off"
              />
              <InputSwitch
                title={t('active')}
                value={formData.is_active}
                onChange={(value) => handleInputChange('is_active', value)}
                horizontal
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
            {t('save')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default OrganizationEditModal
